import { Box, Chip, Pagination, Typography } from "@mui/material";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { searchUsers } from "../../../../api/network";
import SearchUsersOrGroups from "./SearchUsersOrGroups";
import EmptyFollowersListFigure from "./EmptyFollowersListFigure";
import EmptyGroupsListFigure from "./EmptyGroupsListFigure";
import { Group, UserInfo } from "../../../../types";
import typography from "../../../../theme/typography";
import { colors } from "../../../../theme/palette";
import GroupCard from "./GroupCard";
import UserCard from "./UserCard";
import { useCreateEventContext } from "../cxt";
import { getGroupsList } from "../../../../api/groups";
import { useProfileData } from "../../../../hooks/useProfileData";
import SelectedUserCard from "./SelectedUserCard";
import SelectedGroupCard from "./SelectedGroupCard";

const SelectParticipants = () => {
  const { t } = useTranslation();
  const [type, setType] = useState<"FOLLOWERS" | "GROUPS">("FOLLOWERS");
  const [searchText, setSearchText] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);
  const { state, setState } = useCreateEventContext();

  const { data: entityData, isLoading: isEntityDataLoading } = useProfileData();

  const { entityId } = entityData;

  const handleDeleteSelectedUser = (id: number | null) => {
    if (!id) return;

    setState((prevState) => ({
      ...prevState,
      selectedEntities: prevState.selectedEntities.filter((entity: any) => entity.id !== id),
    }));
  };

  const handleDeleteSelectedGroup = (id: number | null) => {
    if (!id) return;

    setState((prevState) => ({
      ...prevState,
      selectedGroups: prevState.selectedGroups.filter((entity: any) => entity.id !== id),
    }));
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["eventsUsersGroupsList", type, searchText, offset, entityId],
    queryFn: async () => {
      if (type === "FOLLOWERS") {
        return await searchUsers(16, offset, searchText, "", "", "", "", "", undefined, true);
      } else if (type === "GROUPS" && entityId) {
        return await getGroupsList(entityId);
        // return await getGroupsList(entityId, 8, offset);
      }
      return [];
    },
    staleTime: 0,
  });

  const items = data?.items || [];
  const meta = data?.meta;

  const groupsList = type === "GROUPS" ? data : [];

  const handleSearchChange = (searchText: string) => {
    setSearchText(searchText);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => setOffset(value - 1);

  return (
    <Box>
      <Typography
        variant="body16Bold"
        sx={{
          display: "block",
          textAlign: "start",
          marginBottom: "35px",
        }}
      >
        {t("pages.events.createEvent.selectParticipants")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          marginBottom: "24px",
        }}
      >
        <Chip
          label={t("pages.events.createEvent.followers")}
          component="button"
          variant="outlined"
          clickable
          onClick={() => setType("FOLLOWERS")}
          sx={{
            border: "none",
            padding: "1px 4px",
            ...(type === "FOLLOWERS" ? { ...typography.body14Regular } : { ...typography.body14Regular }),
            color: type === "FOLLOWERS" ? colors.Green100 : colors.Dark600,
            background: type === "FOLLOWERS" ? colors.Green25 : "transparent",
            fontWeight: type === "FOLLOWERS" ? 600 : 400,
          }}
        />
        <Chip
          label={t("pages.events.createEvent.groups")}
          component="button"
          variant="outlined"
          clickable
          onClick={() => setType("GROUPS")}
          sx={{
            border: "none",
            padding: "1px 4px",
            ...(type === "GROUPS" ? { ...typography.body14Regular } : { ...typography.body14Regular }),
            color: type === "GROUPS" ? colors.Green100 : colors.Dark600,
            background: type === "GROUPS" ? colors.Green25 : "transparent",
            fontWeight: type === "GROUPS" ? 600 : 400,
          }}
        />
      </Box>
      <SearchUsersOrGroups searchType={type} onSearchChange={handleSearchChange} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        <Typography
          variant="body13Bold"
          sx={{
            display: "block",
            textAlign: "start",
            fontWeight: 600,
            color: colors.Green100,
          }}
        >
          {t("pages.events.createEvent.selectedUsers")} {` (${state.selectedEntities.length})`} <br />
          {t("pages.events.createEvent.selectedGroups")} {` (${state.selectedGroups.length})`}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "30px",
          }}
        >
          {state.selectedEntities.map((user) => (
            <SelectedUserCard key={user.id} user={user} onDeleteFunction={handleDeleteSelectedUser} />
          ))}
          {state.selectedGroups.map((group) => (
            <SelectedGroupCard key={group.id} group={group} onDeleteFunction={handleDeleteSelectedGroup} />
          ))}
        </Box>
      </Box>
      {isLoading && <p>Loading...</p>}
      {isError && <p>Error fetching data</p>}
      {(!groupsList || groupsList?.length === 0) && type === "GROUPS" && <EmptyGroupsListFigure />}
      {items?.length === 0 && type === "FOLLOWERS" && <EmptyFollowersListFigure />}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          columnGap: "40px",
          rowGap: "16px",
          marginBottom: "32px",
        }}
      >
        {items?.map((user: UserInfo) => {
          if (type === "FOLLOWERS") return <UserCard key={user.id} user={user} />;
        })}
        {groupsList?.map((group: Group) => {
          if (type === "GROUPS") return <GroupCard key={group.id} group={group} />;
        })}
      </Box>
      {items?.length > 0 && !isLoading && !isError && type !== "GROUPS" && (
        <Pagination
          count={meta?.totalPages}
          page={!meta?.totalItems || meta?.totalItems <= 0 ? 1 : offset + 1}
          onChange={handleChangePage}
          sx={{
            marginBottom: "45px",
            direction: "ltr !important",
            "& .MuiButtonBase-root": {
              borderRadius: "8px !important",
            },
            "& .MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
        />
      )}
    </Box>
  );
};

export default memo(SelectParticipants);
